import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, IconButton, Alert,
  Menu, MenuItem, Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AutocompleteWarehouses from 'elements/AutocompleteWarehouses';
import { ApiRequest } from 'GlobalFunctions';
import SubstitutionDialog, { SubstituteItem } from './SubstitutionDialog';

export interface StockValidatorProduct {
  product_id: number;
  product_name: string;
  product_code?: string;
  quantity: number;
}

interface StockValidatorDialogProps {
  open: boolean;
  onClose: () => void;
  products: StockValidatorProduct[];
  orderIds: number[];
  invoicing?: boolean;
  onConfirm: (warehouseId: string, adjustedProducts: StockValidatorProduct[]) => void;
}

interface StockResult { [productId: string]: number; }
interface SubstitutionsMap { [productId: string]: SubstituteItem[]; }

export default function StockValidatorDialog({
  open, onClose, products, orderIds, invoicing, onConfirm
}: StockValidatorDialogProps) {

  const [warehouseId, setWarehouseId] = useState('');
  const [stockData, setStockData] = useState<StockResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [substitutions, setSubstitutions] = useState<SubstitutionsMap>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [noStockError, setNoStockError] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuProduct, setMenuProduct] = useState<StockValidatorProduct | null>(null);

  // Substitution dialog
  const [subDialogProduct, setSubDialogProduct] = useState<StockValidatorProduct | null>(null);

  useEffect(() => {
    if (!open) { setWarehouseId(''); setStockData(null); setSubstitutions({}); }
  }, [open]);

  const fetchStock = () => {
    if (!warehouseId || !products.length) return;
    setLoading(true);
    ApiRequest({
      url: '/inventory/validate-stock',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: { warehouse_id: warehouseId, product_ids: products.map(p => p.product_id) },
      setResponse: (r: any) => {
        if (r.status === 'success') setStockData(r.stock);
        setLoading(false);
      },
    });
  };

  const fetchSubstitutions = () => {
    if (!orderIds.length || orderIds.length > 1) return;
    ApiRequest({
      url: '/orders/substitutions/get',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: { order_id: orderIds[0] },
      setResponse: (r: any) => {
        if (r.status === 'success') setSubstitutions(r.substitutions || {});
      },
    });
  };

  useEffect(() => {
    if (!warehouseId || !products.length) { setStockData(null); return; }
    fetchStock();
  }, [warehouseId, products]);

  useEffect(() => {
    if (open && orderIds.length === 1) fetchSubstitutions();
  }, [open, orderIds]);

  const isValid = (p: StockValidatorProduct) => {
    const subs = substitutions[p.product_id];
    if (subs) return subs.reduce((s, i) => s + i.quantity, 0) >= p.quantity;
    return stockData ? (stockData[p.product_id] ?? 0) >= p.quantity : false;
  };

  const allValid = stockData ? products.every(isValid) : false;

  const insufficientProducts = stockData
    ? products.filter(p => !isValid(p))
    : [];

  const getAdjustedProducts = (): StockValidatorProduct[] => {
    if (!stockData) return [];
    const result: StockValidatorProduct[] = [];
    for (const p of products) {
      const subs = substitutions[p.product_id];
      if (subs) {
        for (const s of subs) result.push({ product_id: s.product_id, product_name: s.product_name, product_code: s.product_code, quantity: s.quantity });
      } else {
        const available = Math.min(p.quantity, Math.max(0, stockData[p.product_id] ?? 0));
        if (available > 0) result.push({ ...p, quantity: available });
      }
    }
    return result;
  };

  const [invoiceConfirmOpen, setInvoiceConfirmOpen] = useState(false);

  const handleFacturar = () => {
    if (allValid) {
      setInvoiceConfirmOpen(true);
    } else {
      const adjusted = getAdjustedProducts();
      if (adjusted.length === 0) { setNoStockError(true); return; }
      setConfirmOpen(true);
    }
  };

  const handleInvoiceConfirmed = () => {
    setInvoiceConfirmOpen(false);
    onConfirm(warehouseId, getAdjustedProducts());
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, p: StockValidatorProduct) => {
    setMenuAnchor(e.currentTarget);
    setMenuProduct(p);
  };

  const handleOpenSubstitution = () => {
    setMenuAnchor(null);
    if (menuProduct) setSubDialogProduct(menuProduct);
  };

  return (<>
    <Dialog open={noStockError} onClose={() => setNoStockError(false)}>
      <DialogTitle>Error</DialogTitle>
      <DialogContent>
        <Alert severity="error">No es posible facturar. Ningún producto tiene unidades disponibles.</Alert>
      </DialogContent>
      <DialogActions><Button onClick={() => setNoStockError(false)}>Cerrar</Button></DialogActions>
    </Dialog>

    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Stock insuficiente</DialogTitle>
      <DialogContent>
        <Table size="small">
          <TableHead><TableRow><TableCell>Producto</TableCell><TableCell align="center">Faltan</TableCell><TableCell align="center">Pedido</TableCell></TableRow></TableHead>
          <TableBody>
            {insufficientProducts.map(p => {
              const stock = stockData![p.product_id] ?? 0;
              const subs = substitutions[p.product_id];
              const covered = subs ? subs.reduce((s, i) => s + i.quantity, 0) : Math.max(0, stock);
              return (
                <TableRow key={p.product_id}>
                  <TableCell>{p.product_name}</TableCell>
                  <TableCell align="center" sx={{ color: '#c62828', fontWeight: 600 }}>{p.quantity - covered}</TableCell>
                  <TableCell align="center">{p.quantity}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <p style={{ margin: '12px 0 0', fontSize: 14, color: '#555' }}>Al confirmar, se facturarán únicamente las cantidades disponibles. Este proceso no es reversible.</p>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
        <Button variant="contained" color="warning" onClick={() => { setConfirmOpen(false); onConfirm(warehouseId, getAdjustedProducts()); }}>Confirmar y facturar</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={invoiceConfirmOpen} onClose={() => setInvoiceConfirmOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Confirmar facturación</DialogTitle>
      <DialogContent>
        <p style={{ margin: 0, fontSize: 14, color: '#555' }}>Se va a generar la factura electrónica. Este proceso no es reversible.</p>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setInvoiceConfirmOpen(false)}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleInvoiceConfirmed}>Aceptar</Button>
      </DialogActions>
    </Dialog>

    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
      <MenuItem onClick={handleOpenSubstitution}><SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />Sustituir</MenuItem>
    </Menu>

    {subDialogProduct && orderIds.length > 0 && (
      <SubstitutionDialog
        open={Boolean(subDialogProduct)}
        onClose={() => setSubDialogProduct(null)}
        orderId={orderIds.length === 1 ? orderIds[0] : undefined}
        product={subDialogProduct}
        warehouseId={warehouseId}
        availableStock={stockData ? (stockData[subDialogProduct.product_id] ?? 0) : 0}
        orderProducts={products.map(p => ({ product_id: p.product_id, quantity: p.quantity }))}
        initialItems={substitutions[subDialogProduct.product_id]}
        onSaved={(items) => {
          if (items) {
            // Batch mode: update local state
            setSubstitutions(prev => ({ ...prev, [subDialogProduct.product_id]: items }));
          } else {
            // Single mode: reload from server
            fetchSubstitutions();
          }
        }}
      />
    )}

    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Validación de existencias — {orderIds.length} pedido{orderIds.length > 1 ? 's' : ''}</DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, marginTop: 8, maxWidth: 320 }}>
          <AutocompleteWarehouses value={warehouseId} onChange={(e: any) => setWarehouseId(e.target.value)} />
          {warehouseId && <IconButton onClick={fetchStock} size="small" title="Actualizar"><RefreshIcon /></IconButton>}
        </div>

        {warehouseId && (
          loading ? (
            <div style={{ textAlign: 'center', padding: 24 }}><CircularProgress size={28} /></div>
          ) : stockData && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="center">Pedido</TableCell>
                    <TableCell align="center">Disponible</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell width={40}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map(p => {
                    const stock = stockData[p.product_id] ?? 0;
                    const subs = substitutions[p.product_id];
                    const valid = isValid(p);
                    const hasSubs = Boolean(subs && subs.length);
                    const insufficient = stock < p.quantity;
                    return (
                      <>
                        <TableRow key={p.product_id}>
                          <TableCell>
                            <span style={{ fontWeight: 500 }}>{p.product_name}</span>
                            {p.product_code && <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>{p.product_code}</span>}
                            {hasSubs && <Chip label="Cambio" size="small" icon={<SwapHorizIcon />} sx={{ ml: 1, height: 20, fontSize: 11 }} color="info" variant="outlined" />}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>{p.quantity}</TableCell>
                          <TableCell align="center" sx={{ color: valid ? '#2e7d32' : '#c62828', fontWeight: 600 }}>{hasSubs ? subs!.reduce((s, i) => s + i.quantity, 0) : stock}</TableCell>
                          <TableCell align="center">
                            {valid ? <CheckCircleIcon sx={{ color: '#4caf50' }} /> : <CancelIcon sx={{ color: '#f44336' }} />}
                          </TableCell>
                          <TableCell align="center">
                            {insufficient && <IconButton size="small" onClick={(e) => handleMenuOpen(e, p)}><MoreVertIcon fontSize="small" /></IconButton>}
                          </TableCell>
                        </TableRow>
                        {hasSubs && subs!.map((s, i) => (
                          <TableRow key={`${p.product_id}-sub-${i}`} sx={{ bgcolor: '#f9f9f9' }}>
                            <TableCell sx={{ pl: 5, fontSize: 13, color: '#555' }}>↳ {s.product_name} {s.product_code && <span style={{ color: '#aaa', fontSize: 11 }}>{s.product_code}</span>}</TableCell>
                            <TableCell align="center" sx={{ fontSize: 13 }}>{s.quantity}</TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        ))}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={invoicing}>Cancelar</Button>
        <Button variant="contained" disabled={!stockData || invoicing} onClick={handleFacturar}>
          {invoicing ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {invoicing ? 'Facturando...' : 'Generar factura'}
        </Button>
      </DialogActions>
    </Dialog>
  </>);
}
